import { Avatar, Box, Divider, Flex } from '@chakra-ui/react';
import { KText } from '@/ui-library/components';
import { SectionLabel } from '@kula-ai/components';
import {
  ScorecardItem,
  ScorecardQuestion,
} from '../GenerateScorecard.interface';
import { ScorecardPDFRating } from './ScorecardPDFRating';
import moment from 'moment-timezone';
import { sanitizeHtml } from '@/lib/purify';

interface ScorecardPDFItemProps {
  scorecard: ScorecardItem;
  index: number;
  hideActivityName?: boolean;
  pdfSectionTitle?: string;
  timezone?: string;
  dateTimeFormat?: string;
}

const renderTagValue = (value: string) => (
  <Flex
    alignItems="center"
    p="2px 8px"
    gap="4px"
    bg="primary-50"
    border="1px solid"
    borderColor="border-color"
    borderRadius="6px"
    w="fit-content"
  >
    <KText size="sm" color="primary-600">
      {value}
    </KText>
  </Flex>
);

const renderTextValue = (value: string) => (
  <Box
    dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
    maxW="100%"
    fontSize="14px"
    fontWeight="400"
    color="secondary-500"
    lineHeight="160%"
    wordBreak="break-word"
    overflowWrap="break-word"
  />
);

const QuestionAnswer = ({
  question,
}: {
  question: ScorecardQuestion;
  index: number;
}) => {
  const {
    question: questionText,
    answer,
    type,
    mandatory,
    description,
  } = question;

  const renderAnswer = () => {
    if (!answer) {
      return <KText color="secondary-500">Not answered</KText>;
    }

    switch (type) {
      case 'yes_no':
        return renderTagValue(answer === 'yes' ? 'Yes' : 'No');

      case 'one_to_ten':
        return renderTagValue(`${answer} (out of 10)`);

      case 'star':
        return renderTagValue(`${answer} (out of 5)`);

      case 'open_question':
        return renderTextValue(answer);

      case 'multiple_choice':
      case 'checkbox':
        return renderTagValue(answer);

      default:
        return renderTagValue(answer);
    }
  };

  return (
    <>
      <Flex flexDir="column" gap="6px">
        {/* Question Label */}
        <Flex gap="2px" alignItems="flex-start">
          <KText color="secondary-900" w="fit-content">
            {questionText}
          </KText>
          {(mandatory === true || mandatory === 'true') && (
            <KText color="error-200">*</KText>
          )}
        </Flex>

        {/* Description */}
        {description && (
          <KText size="xs-regular" color="secondary-500">
            {description}
          </KText>
        )}

        {/* Answer */}
        <Box>{renderAnswer()}</Box>
      </Flex>
      <Divider
        orientation="horizontal"
        borderColor="border-color"
        borderWidth="1px"
      />
    </>
  );
};

const DEFAULT_DATE_TIME_FORMAT = 'DD/MM/YY - h:mmA';

export const ScorecardPDFItem = ({
  scorecard,
  hideActivityName = false,
  pdfSectionTitle,
  timezone: tz,
  dateTimeFormat = DEFAULT_DATE_TIME_FORMAT,
}: ScorecardPDFItemProps) => {
  const {
    record,
    record_type,
    reviewer,
    submitted_by,
    submitted_at,
    rating,
    feedback,
    questions,
  } = scorecard;

  const submitter = submitted_by;
  const reviewerOrInterviewer = reviewer;
  const isSameAsSubmitter =
    !!reviewerOrInterviewer?.id &&
    !!submitter?.id &&
    reviewerOrInterviewer.id === submitter.id;

  const submittedInfoBar = (
    <Flex
      bg="secondary-25"
      p="12px 16px"
      gap="32px"
      alignItems="flex-start"
      borderRadius="6px"
      mb="16px"
    >
      {!isSameAsSubmitter && (
        <Flex flexDir="column" gap="4px" overflow="hidden" minW={0}>
          <KText color="secondary-900">
            {record_type === 'AtsInterview' ? 'Interviewer' : 'Reviewer'}
          </KText>
          <Flex gap="6px" alignItems="center" overflow="hidden" minW={0}>
            <Avatar
              w="20px"
              h="20px"
              borderRadius="3px"
              src={reviewerOrInterviewer?.avatar || undefined}
              name={reviewerOrInterviewer?.name}
              className="avatar-xs"
              flexShrink={0}
              overflow="hidden"
              ignoreFallback
            />
            <KText
              color="secondary-500"
              overflow="hidden"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              minW={0}
            >
              {reviewerOrInterviewer?.name}
            </KText>
          </Flex>
        </Flex>
      )}

      <Flex flexDir="column" gap="4px" overflow="hidden" minW={0}>
        <KText color="secondary-900">Submitted by</KText>
        <Flex gap="6px" alignItems="center" overflow="hidden" minW={0}>
          <Avatar
            w="20px"
            h="20px"
            borderRadius="3px"
            src={submitter?.avatar || undefined}
            name={submitter?.name}
            className="avatar-xs"
            flexShrink={0}
            overflow="hidden"
            ignoreFallback
          />
          <KText
            color="secondary-500"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            minW={0}
          >
            {submitter?.name}
          </KText>
        </Flex>
      </Flex>

      <Flex flexDir="column" gap="4px" flexShrink={0}>
        <KText color="secondary-900">When</KText>
        <KText color="secondary-500">
          {submitted_at
            ? moment
                ?.utc(submitted_at)
                ?.tz(tz || 'UTC')
                ?.format(dateTimeFormat)
            : '-'}
        </KText>
      </Flex>
    </Flex>
  );

  const ratingBeforeFeedback = (
    <Flex
      flexDir="column"
      gap="6px"
      pl="6px"
      className="scorecard-pdf-body-rating"
      w="fit-content"
    >
      <KText color="secondary-900" w="fit-content">
        Rating
      </KText>
      <ScorecardPDFRating rating={rating} size="sm" />
    </Flex>
  );

  // Get activity name based on record type
  const getActivityName = () => {
    if (record_type === 'AtsInterview') {
      return 'Phone Call Interview';
    }
    if (record_type === 'AtsReview') {
      return 'Review';
    }
    if (record_type === 'AtsAssessment') {
      return 'Assessment';
    }
    return record?.name || '';
  };

  return (
    <Flex flexDir="column" className="scorecard-item">
      {/* activity name - only show if not hidden */}
      {!hideActivityName && (
        <Flex alignItems="center" gap="12px" mb="8px">
          <Flex
            alignItems="center"
            gap="6px"
            bg="secondary-50"
            px="6px"
            py="4px"
            borderRadius="6px"
          >
            <KText as="span" size="sm-medium" color="secondary-900">
              {getActivityName()}
            </KText>
          </Flex>
          <Box borderBottom="1px solid" borderColor="border-color" flex={1} />
        </Flex>
      )}

      {/* Content Container */}
      <Flex flexDir="column" p={hideActivityName ? '0' : '16px 0'}>
        {pdfSectionTitle ? (
          <Flex
            flexDir="column"
            gap="12px"
            className="scorecard-pdf-lead-cluster"
          >
            <SectionLabel
              size="lg"
              showDivider
              labelClassName="text-secondary-700"
            >
              {pdfSectionTitle}
            </SectionLabel>
            {submittedInfoBar}
          </Flex>
        ) : (
          submittedInfoBar
        )}

        {/* Questions */}
        {questions?.length > 0 && (
          <Flex flexDir="column" gap="16px" pl="6px">
            {questions.map((question, qIndex) => (
              <QuestionAnswer key={qIndex} question={question} index={qIndex} />
            ))}
          </Flex>
        )}

        {/* Rating — below questions, before overall feedback (not in top bar) */}
        <Box pt={questions?.length ? '16px' : 0}>{ratingBeforeFeedback}</Box>

        {/* Feedback */}
        {feedback && (
          <Flex flexDir="column" gap="6px" pt="16px" pl="6px">
            <KText color="secondary-900" w="fit-content">
              Overall feedback
            </KText>
            {renderTextValue(feedback)}
          </Flex>
        )}

        {/* Empty state */}
        {!questions?.length && !feedback && (
          <KText size="sm" color="secondary-500" pl="6px" pt="8px">
            No details provided
          </KText>
        )}
      </Flex>
    </Flex>
  );
};
